/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import dayjs from 'dayjs';
import clsx, { ClassValue } from 'clsx';

import 'react-datepicker/dist/react-datepicker.css';
import {
  TextFieldProps,
  inputBaseClasses,
  inputSizeClasses,
} from '../TextField';

import { minDate, getOffset, getTimeZone } from 'src/utils/datepickerHelper';

type DatepickerType = {
  id?: any;
  placeholder?: string;
  className?: ClassValue;
  value?: any;
  format?: string;
  onChange?: (e: any) => void;
  inputSize?: TextFieldProps['inputSize'];
  hasMaxDate?: boolean;
  container?: any;
  timeZone?: any;
  showTimePicker?: any;
  showTimeInput?: any;
  shouldCloseOnSelect?: boolean;
  timeFormat?: string;
  dateFormat?: string;
  minimumDate?: Date;
  maximumDate?: Date;
  disabled?: boolean;
};

function TimezoneIntegratedDatePicker({
  value,
  placeholder,
  className,
  inputSize = 'medium',
  onChange,
  hasMaxDate = false,
  container,
  timeZone,
  showTimePicker,
  showTimeInput,
  shouldCloseOnSelect,
  timeFormat,
  dateFormat,
  minimumDate,
  maximumDate,
  disabled,
}: DatepickerType) {
  const getMaxDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  };

  const userClasses = clsx(className);

  //to close date picker on scroll
  const datePickerRef = useRef<any>();
  useEffect(() => {
    const handleScroll = () => {
      if (container && container?.current && datePickerRef?.current) {
        datePickerRef?.current?.setOpen(false);
      }
    };

    if (container && container?.current) {
      container?.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container && container?.current) {
        container?.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  function onKeyDownHandler(e: React.KeyboardEvent<HTMLElement>) {
    const allowedKeys = [
      'Backspace',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Delete',
      '/',
    ];

    const isNumber = /^[0-9]$/.test(e.key);

    if (e.key === 'Tab') {
      datePickerRef.current.setOpen(false);
      // Find the next focusable element dynamically
      const nextElement = document?.activeElement?.nextElementSibling;
      if (nextElement) {
        // Ensure that the nextElement is an HTMLElement before calling focus
        if (nextElement instanceof HTMLElement) {
          nextElement.focus();
        }
      }
      return;
    }

    if (!isNumber && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  }

  // Get the current date

  let offset = timeZone !== undefined ? getOffset(timeZone) : getTimeZone();

  return (
    <DatePicker
      id="calendar"
      selected={value}
      onChange={(e: any) => {
        onChange && onChange(e);
      }}
      dateFormat={dateFormat ? dateFormat : 'MM/dd/yyyy'}
      placeholderText={placeholder}
      className={`${userClasses} ${inputBaseClasses} ${
        inputSize === 'small'
          ? inputSizeClasses.small
          : inputSize === 'medium'
          ? inputSizeClasses.medium
          : inputSize === 'large'
          ? inputSizeClasses.large
          : ''
      }`}
      maxDate={
        maximumDate ? maximumDate : hasMaxDate ? undefined : getMaxDate()
      }
      minDate={minimumDate ? minimumDate : minDate(offset)}
      ref={datePickerRef}
      showTimeSelect={showTimePicker}
      showTimeInput={showTimeInput}
      shouldCloseOnSelect={shouldCloseOnSelect}
      timeFormat={timeFormat}
      disabled={disabled}
      onKeyDown={e => onKeyDownHandler(e)}
      onCalendarOpen={() => {
        //setting the startdate to mindate
        datePickerRef.current.state.preSelection = new Date(minDate(offset));
      }}
    />
  );
}

export { TimezoneIntegratedDatePicker };
